import get from "lodash/get";
import omit from "lodash/omit";
import set from "lodash/set";

import { ILibraryState } from "./ILibraryState";
import { IFolder } from "./IFolder";
import { IBookInfo } from "./IBookInfo";

import { AuthChangedSuccess } from "../session/actionTypes";
import { SessionTypes } from "../session/constants";

import {
  ROOT_FOLDER,
  REQUEST_LIBRARY_SUCCESS,
  SET_ACTIVE_FOLDER,
  SUBSCRIBE_TO_FOLDER_SUCCESS,
  SUBSCRIBE_TO_FOLDER_ERROR,
  SET_ACTIVE_RECORD,
  SUBSCRIBE_TO_RECORD_ERROR,
  SUBSCRIBE_TO_RECORD_SUCCESS,
} from "./constants";
import {
  RequestLibrarySuccess,
  SubscribeToFolderSuccess,
  SubscribeToFolderError,
  LibraryActions,
  SetActiveFolder,
  SetActiveRecord,
  SubscribeToRecordError,
  SubscribeToRecordSuccess,
} from "./actionTypes";

import { convertTreeToList } from "../../../utils/convertTreeToList";
import { normalizeTimestamp } from "../../../utils/normalizeTimestamp";

const initState: ILibraryState = {
  activeFolder: undefined,
  activeRecord: undefined,
  books: {},
  folders: {},
};

type Actions = LibraryActions | AuthChangedSuccess;
export function libraryReducer(state = initState, { type, ...action }: Actions): ILibraryState {
  switch (type) {
    case SET_ACTIVE_FOLDER: {
      const { payload } = action as SetActiveFolder;

      return ({
        ...state,
        activeFolder: payload,
      });
    }

    case SET_ACTIVE_RECORD: {
      const { payload } = action as SetActiveRecord;

      return ({
        ...state,
        activeRecord: payload,
      });
    }

    case REQUEST_LIBRARY_SUCCESS: {
      const { payload } = action as RequestLibrarySuccess;

      return ({
        ...state,
        folders: convertTreeToList(ROOT_FOLDER, [], payload),
      });
    }

    case SUBSCRIBE_TO_FOLDER_SUCCESS: {
      const { payload: { folderId, folder } } = action as SubscribeToFolderSuccess;
      const { folders: prevFolders, books: prevBooks } = state;
      const currentFolder: IFolder = {
        ...prevFolders[folderId],
        isContentLoaded: true,
        title: folder.title,
        isWasOpened: folder.isWasOpened,
        isArchived: folder.isArchived,
        dateAdded: folder.dateAdded,
        lastOpenDate: folder.lastOpenDate,
      };
      const foldersPatch: Record<string, IFolder> = {};
      const booksPatch: Record<string, IBookInfo> = {};

      if (folder.folders) {
        Object.entries(folder.folders).forEach(([subFolderId, subFolder]) => {
          if (!subFolder.isArchived) {
            currentFolder.folders[subFolderId] = subFolderId;
          }

          foldersPatch[subFolderId] = prevFolders[subFolderId]
            ? ({
              ...prevFolders[subFolderId],
              title: subFolder.title,
              isWasOpened: subFolder.isWasOpened,
              isArchived: subFolder.isArchived,
              dateAdded: subFolder.dateAdded,
              lastOpenDate: subFolder.lastOpenDate,
            })
            : ({
              id: subFolderId,
              title: subFolder.title,
              path: [...currentFolder.path, folderId],
              folders: {},
              books: {},
              isContentLoaded: false,
              isWasOpened: subFolder.isWasOpened,
              isArchived: subFolder.isArchived,
              dateAdded: subFolder.dateAdded,
              lastOpenDate: subFolder.lastOpenDate,
            });
        });
      }

      if (folder.books) {
        Object.entries(folder.books).forEach(([bookId, book]) => {
          if (!book.isArchived) {
            currentFolder.books[bookId] = bookId;
          }

          const timestamps = ["dateAdded", "lastOpenDate", "dateRemoved"].reduce(
            (acc, key) => {
              const value = get(book, key);

              if (value) {
                set(acc, key, normalizeTimestamp(value));
              }

              return acc;
            },
            {}
          );

          const commonFields = {
            path: [...currentFolder.path, folderId],
            ...timestamps,
          };

          booksPatch[bookId] = prevBooks[bookId]
            ? ({
              ...prevBooks[bookId],
              ...omit(book, ["text"]),
              ...commonFields,
            })
            : ({
              id: bookId,
              ...book,
              ...commonFields,
            });
        });
      }

      return ({
        ...state,
        folders: {
          ...prevFolders,
          ...foldersPatch,
          [folderId]: currentFolder,
        },
        books: {
          ...prevBooks,
          ...booksPatch,
        },
      });
    }

    case SUBSCRIBE_TO_FOLDER_ERROR: {
      const { payload: folderId } = action as SubscribeToFolderError;
      const { folders } = state;

      return ({
        ...state,
        folders: {
          ...folders,
          [folderId]: {
            id: folderId,
            isContentLoaded: true,
            error: true,
          } as IFolder,
        },
      });
    }

    case SUBSCRIBE_TO_RECORD_SUCCESS: {
      const { payload: { recordId, book } } = action as SubscribeToRecordSuccess;
      const { books } = state;

      const timestamps = ["dateAdded", "lastOpenDate", "dateRemoved"].reduce(
        (acc, key) => {
          const value = get(book, key);

          if (value) {
            set(acc, key, normalizeTimestamp(value));
          }

          return acc;
        },
        {}
      );

      return ({
        ...state,
        books: {
          ...books,
          [recordId]: {
            text: "",
            path: [],
            ...books[recordId],
            ...book,
            ...timestamps,
            id: recordId,
          },
        },
      });
    }

    case SUBSCRIBE_TO_RECORD_ERROR: {
      const { payload: recordId } = action as SubscribeToRecordError;
      const { books } = state;

      return ({
        ...state,
        books: {
          ...books,
          [recordId]: {
            ...books[recordId],
            error: true,
          },
        },
      });
    }

    case SessionTypes.AUTH_CHANGED_SUCCESS: {
      return initState;
    }

    default:
      return state;
  }
}
